import { useEffect, useRef, useState } from 'react';
import { T } from '../../../../theme/tokens';
import { localDay } from '../../../../utils/date';

/* The window the screen reports on.

   Avi, 14/10, in this order: "נפתח על החודש — מה היה מתחילת החודש עד אותו יום",
   then "אם תיתן לי את הגמישות לשחק עם התאריכים כמו שאני רוצה", then "אני רוצה
   לראות מה קרה מתחילת השנה", then "לסמן, נגיד, מינואר עד ספטמבר". Those are the
   four shapes below, and month-to-date is the default because it is the one he
   named for opening the screen.

   🔴 Comparing a part-month against a full month is the easiest way to read a
   false collapse, which is why the current month stops at today rather than
   running to the 31st with zeros in the tail. */

export interface Range { from: string; to: string }

/* 🔴 לא toISOString. ראה utils/date — הוא ממיר ל-UTC, וחצות מקומי הוא
   היום הקודם שם. ברירת המחדל של המסך ביקשה 2026-09-30 במקום 2026-10-01. */
const iso = localDay;
/** 2026-09-30 -> 30/09/2026, the format used everywhere else in the product. */
const il = (s: string) => s.split('-').reverse().join('/');
const HE_MONTH = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
                  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

export function monthToDate(): Range {
  const now = new Date();
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
}

function wholeMonth(y: number, m: number): Range {
  const now = new Date();
  const last = new Date(y, m + 1, 0);
  // The current month stops at today. A month still being lived is not a short
  // month, and drawing it as one is how "August collapsed" gets said out loud.
  const end = (y === now.getFullYear() && m === now.getMonth()) ? now : last;
  return { from: iso(new Date(y, m, 1)), to: iso(end) };
}

function yearToDate(): Range {
  const now = new Date();
  return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
}

type Mode = 'mtd' | 'month' | 'ytd' | 'custom';

/** The label shown on the closed button, for a range the user already picked. */
function describe(r: Range): string {
  const f = new Date(r.from), t = new Date(r.to);
  const now = new Date();
  const mtd = monthToDate();
  if (r.from === mtd.from && r.to === mtd.to) return `${HE_MONTH[now.getMonth()]} עד היום`;
  if (r.from === yearToDate().from && r.to === mtd.to) return 'מתחילת השנה';
  if (f.getFullYear() === t.getFullYear() && f.getMonth() === t.getMonth()
      && f.getDate() === 1 && t.getDate() === new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate()) {
    return `${HE_MONTH[f.getMonth()]} ${f.getFullYear()}`;
  }
  /* 🔴 The house date format, and never two ISO dates joined by a dash inside an
     RTL line: bidi reorders the pair and the range reads backwards — the same
     failure as a reversed time range. `il()` emits DD/MM/YYYY, and the caller
     wraps the result in dir="ltr" so the two halves keep their order. */
  return `${il(r.from)} — ${il(r.to)}`;
}

export function DateRange({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('mtd');
  const [draft, setDraft] = useState<Range>(value);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const pick = (r: Range) => { onChange(r); setOpen(false); };

  const now = new Date();
  const recent = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const tab = (id: Mode, label: string) => (
    <button
      key={id} onClick={() => setMode(id)}
      style={{
        fontSize: 11.5, fontWeight: mode === id ? 700 : 500, border: 'none', cursor: 'pointer',
        color: mode === id ? '#FFFFFF' : T.inkSoft,
        background: mode === id ? T.ink : 'transparent',
        borderRadius: 6, padding: '6px 9px', whiteSpace: 'nowrap', flex: 1,
      }}
    >
      {label}
    </button>
  );

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          fontSize: 12.5, fontWeight: 700, color: T.ink, background: T.card,
          border: `1px solid ${T.border}`, borderRadius: 7,
          padding: '6px 11px', cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        <span aria-hidden style={{ color: T.blue }}>▤</span>
        <span dir="ltr">{describe(value)}</span>
        <span aria-hidden style={{ fontSize: 9, color: T.inkFaint }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', insetInlineStart: 0, zIndex: 50,
          background: T.card, border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
          boxShadow: T.shadowRaised, padding: 10, minWidth: 322,
        }}>
          <div style={{ display: 'flex', gap: 3, background: T.bg, borderRadius: 7, padding: 3, marginBottom: 10 }}>
            {tab('mtd', 'החודש')}
            {tab('month', 'חודש מלא')}
            {tab('ytd', 'מתחילת השנה')}
            {tab('custom', 'טווח חופשי')}
          </div>

          {mode === 'mtd' && (
            <button onClick={() => pick(monthToDate())} style={rowBtn}>
              {HE_MONTH[now.getMonth()]} — מה-1 ועד היום
            </button>
          )}

          {mode === 'ytd' && (
            <button onClick={() => pick(yearToDate())} style={rowBtn}>
              1 בינואר {now.getFullYear()} ועד היום
            </button>
          )}

          {mode === 'month' && (
            <div style={{ maxHeight: 230, overflowY: 'auto' }}>
              {recent.map(({ y, m }) => (
                <button key={`${y}-${m}`} onClick={() => pick(wholeMonth(y, m))} style={rowBtn}>
                  {HE_MONTH[m]} {y}
                  {y === now.getFullYear() && m === now.getMonth() && (
                    <span style={{ color: T.inkFaint, fontWeight: 400 }}> · עד היום</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {mode === 'custom' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={lbl}>
                <span>מתאריך</span>
                <input type="date" value={draft.from} max={draft.to}
                       onChange={(e) => setDraft({ ...draft, from: e.target.value })} style={inp} />
              </label>
              <label style={lbl}>
                <span>עד תאריך</span>
                <input type="date" value={draft.to} min={draft.from}
                       onChange={(e) => setDraft({ ...draft, to: e.target.value })} style={inp} />
              </label>
              <button
                onClick={() => draft.from && draft.to && pick(draft)}
                disabled={!draft.from || !draft.to}
                style={{
                  fontSize: 12.5, fontWeight: 700, color: '#FFFFFF', background: T.blue,
                  border: 'none', borderRadius: 7, padding: '7px 0', cursor: 'pointer',
                }}
              >
                הצג
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const rowBtn: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'start', fontSize: 12.5, fontWeight: 600,
  color: T.ink, background: 'none', border: 'none', borderRadius: 6,
  padding: '7px 9px', cursor: 'pointer',
};

const lbl: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 10, fontSize: 12, color: T.inkSoft,
};

const inp: React.CSSProperties = {
  fontSize: 12.5, color: T.ink, border: `1px solid ${T.border}`,
  borderRadius: 6, padding: '5px 8px',
};
